import api from '../../services/Api';
import {
  Area,
  Atividade,
  CalendarioFeriados,
  ChecklistItem,
  CicloFechamento,
  Comentario,
  Dependencia,
  Empresa,
  Evidencia,
  Excecao,
  Feriado,
  GrupoEconomico,
  GrupoEmpresas,
  LogAtividade,
  UsuarioMaestro,
} from './models';
import { AcaoPDCA, EtapaPDCA, PontuacaoArea } from './models/inteligencia';
import { ConfiguracaoCanais, EntregaNotificacao, RegraNotificacao, TemplateNotificacao } from './models/notificacoes';
import { Risco, RiscoEscalationEvent } from './models/risco';
import { Delegacao } from './models/delegacao';
import { EtapaSetup, TipoEtapaSetup } from './models/ciclo';
import { RelatorioMensal } from './models/relatorio';
import { LogEntry, OrigemLog, SeveridadeLog } from './models/logs';
import { MotorConciliacaoDados, MapaResultadosDados, RevisorContabilDados, WorkflowSoxDados } from './models/produtos';
import { DESCRICAO_ETAPA_SETUP, LABEL_ETAPA_SETUP, LABEL_PERFIL_USUARIO } from './utils/labels';
import { EscopoUsuarioMaestro, NivelAcessoMaestro, PerfilUsuario } from './models/scope';

const BASE = 'maestro';

// ─── adapters ─────────────────────────────────────────────────────────────────
// Cada adapter converte o DTO real do backend (camelCase, vindo do System.Text.Json) para o
// shape de modelo já usado pelas telas do Cockpit. Campos que o backend simplesmente não tem
// (ver seção 9.37 do handoff) recebem um valor neutro explícito — nunca um valor fantasiado.

function adaptGrupoEconomico(r: any): GrupoEconomico {
  return {
    id: r.id,
    nome: r.nome ?? '—',
    grupoEmpresasIds: r.grupoEmpresasIds ?? [],
    responsavelId: r.responsavelId ?? 0,
  };
}

function adaptGrupoEmpresas(r: any): GrupoEmpresas {
  return {
    id: r.id,
    nome: r.nome ?? '—',
    grupoEconomicoId: r.grupoEconomicoId,
    empresaIds: r.empresaIds ?? [],
  };
}

function adaptEmpresa(r: any, grupoEconomicoId: number, grupoEmpresasIdPorEmpresa: Map<number, number>): Empresa {
  return {
    id: r.id,
    nome: r.razaoSocial || r.nomeFantasia || r.cnpj || '—',
    cnpj: r.cnpj ?? '',
    grupoEmpresasId: grupoEmpresasIdPorEmpresa.get(r.id) ?? 0,
    grupoEconomicoId,
    // Sem RegimeTributario no cadastro de Empresa da plataforma ainda — deixar vazio (não um
    // placeholder "—", que renderizava como um traço fantasma sem sentido nas telas do Maestro).
    regimeTributario: '',
  };
}

function adaptArea(r: any): Area {
  return {
    id: r.id,
    tipo: r.tipo,
    nome: r.nome ?? '—',
    responsavelId: r.responsavelId ?? 0,
    empresaIds: r.empresaIds ?? [],
    cor: r.cor ?? '#a1a5b7',
  };
}

// Usuário "cru" da plataforma (Usuario/BuscarPorClienteID) — usado só como picker de "quem existe
// no cliente" ao adicionar um novo membro do Maestro; nunca usado nos seletores de responsável do
// módulo (ver adaptMembroMaestro).
function adaptUsuario(r: any): UsuarioMaestro {
  const nome: string = r.nome ?? r.username ?? '—';
  const iniciais = nome.split(' ').filter(Boolean).slice(0, 2).map((p: string) => p[0]?.toUpperCase() ?? '').join('');
  return {
    id: r.id,
    nome,
    iniciais: iniciais || '—',
    cargo: r.perfil ?? '—',
    perfil: 'VISUALIZADOR',
    areaId: 0,
    email: r.email ?? '',
    ativo: r.ativo !== false,
  };
}

// Membro real do Maestro (MaestroUsuarioMembroResponseDTO) — só quem está vinculado aqui aparece
// nos seletores de responsável/preparador/revisor/aprovador do módulo. `id` mapeado é o do usuário
// (usuarioId), não o do vínculo — mantém compatibilidade com todo código que já faz getUsuario(id).
function adaptMembroMaestro(r: any): UsuarioMaestro {
  const nome: string = r.nome ?? '—';
  const iniciais = nome.split(' ').filter(Boolean).slice(0, 2).map((p: string) => p[0]?.toUpperCase() ?? '').join('');
  return {
    id: r.usuarioId,
    nome,
    iniciais: iniciais || '—',
    cargo: LABEL_PERFIL_USUARIO[r.perfil as PerfilUsuario] ?? r.perfil ?? '—',
    perfil: (r.perfil as PerfilUsuario) ?? 'VISUALIZADOR',
    areaId: r.areaId ?? 0,
    email: r.email ?? '',
    ativo: r.ativo !== false,
  };
}

// Escopo de acesso (MaestroUserScopeResponseDTO) — a mesma entidade cobre ativo e revogado; `ativo`
// é derivado no cliente da ausência de RevogadoEm, o backend não devolve esse booleano pronto.
function adaptEscopoUsuario(r: any): EscopoUsuarioMaestro {
  return {
    id: r.id,
    usuarioId: r.usuarioId,
    empresaId: r.empresaId,
    areaId: r.areaId ?? undefined,
    nivel: (r.nivel as NivelAcessoMaestro) ?? 'CONSULTA',
    criadoPorUsuarioId: r.criadoPorUsuarioId,
    criadoEm: r.criadoEm,
    revogadoPorUsuarioId: r.revogadoPorUsuarioId ?? undefined,
    revogadoEm: r.revogadoEm ?? undefined,
    ativo: !r.revogadoEm,
  };
}

function adaptFeriado(r: any, tipo: Feriado['tipo'], empresaId: number | undefined): Feriado {
  return { id: r.id, data: r.data, nome: r.nome ?? '—', tipo, empresaId };
}

function adaptCalendario(r: any): CalendarioFeriados {
  return {
    id: r.id,
    nome: r.nome ?? '—',
    tipo: (r.tipo as CalendarioFeriados['tipo']) ?? 'NACIONAL',
    uf: r.uf ?? undefined,
    municipio: r.municipio ?? undefined,
    empresaId: r.empresaId ?? undefined,
    versao: r.versao ?? 1,
    versaoAnteriorId: r.versaoAnteriorId ?? undefined,
    status: (r.status as CalendarioFeriados['status']) ?? 'DRAFT',
    feriados: (r.feriados ?? []).map((f: any) => ({ id: f.id, data: f.data, nome: f.nome ?? '—' })),
    criadoEm: r.criadoEm,
    publicadoEm: r.publicadoEm ?? undefined,
  };
}

function adaptCiclo(r: any): CicloFechamento {
  const statusMap: Record<string, CicloFechamento['status']> = {
    NAO_INICIADO: 'NAO_INICIADO', ABERTO: 'ABERTO', EM_ANDAMENTO: 'EM_ANDAMENTO',
    EM_REVISAO: 'EM_REVISAO', ENCERRADO: 'ENCERRADO', REABERTO: 'REABERTO',
  };
  return {
    id: r.id,
    competencia: r.competencia ?? '—',
    empresaIds: r.empresaIds ?? [],
    dataAbertura: r.dataAbertura ?? '',
    prazoOficial: r.prazoOficial ?? '',
    previsaoConclusao: r.previsaoConclusao ?? r.prazoOficial ?? '',
    dataEncerramentoReal: r.dataEncerramentoReal ?? undefined,
    status: statusMap[r.status] ?? 'NAO_INICIADO',
    progressoEsperado: r.progressoEsperado ?? 0,
    progressoRealizado: r.progressoRealizado ?? 0,
    desvio: r.desvio ?? 0,
    diasUteisRestantes: 0,
    responsavelAberturaId: r.responsavelAberturaId ?? 0,
    responsavelEncerramentoId: r.responsavelEncerramentoId ?? undefined,
    ultimaAtualizacao: r.ultimaAtualizacao ?? r.dataAbertura ?? '',
    encerramentoExcepcional: r.encerramentoExcepcional
      ? {
          justificativa: r.encerramentoExcepcional.justificativa ?? '',
          autorizadorId: r.encerramentoExcepcional.autorizadorId ?? 0,
          aprovado: r.encerramentoExcepcional.aprovado ?? false,
        }
      : undefined,
  };
}

function adaptAtividade(r: any): Atividade {
  return {
    id: r.id,
    codigo: r.codigo ?? '',
    cicloId: r.cicloId,
    nivelHierarquico: r.nivelHierarquico,
    parentId: r.parentId ?? null,
    empresaId: r.empresaId,
    areaId: r.areaId,
    processo: r.processo ?? '',
    nome: r.nome ?? '—',
    descricao: r.descricao ?? '',
    responsavelId: r.responsavelId ?? 0,
    preparadorId: r.preparadorId ?? undefined,
    revisorId: r.revisorId ?? undefined,
    aprovadorId: r.aprovadorId ?? undefined,
    status: r.status,
    prioridade: r.prioridade ?? 'NORMAL',
    criticidade: r.criticidade ?? 'MEDIA',
    progresso: r.progresso ?? 0,
    dataPlanejada: r.dataPlanejada ?? '',
    dataPrevista: r.dataPrevista ?? '',
    dataRealizada: r.dataRealizada ?? undefined,
    prazoDiasUteis: r.prazoDiasUteis ?? 0,
    dependenciaIds: r.dependenciaIds ?? [],
    caminhoCritico: r.caminhoCritico ?? false,
    tipoReceita: r.tipoReceita ?? undefined,
    produtoIntegradoTipo: r.produtoIntegradoTipo ?? undefined,
    impactoNoFechamento: r.impactoNoFechamento ?? '',
    proximaAcao: r.proximaAcao ?? '',
  };
}

function adaptChecklistItem(r: any): ChecklistItem {
  return {
    id: r.id,
    atividadeId: r.atividadeId,
    descricao: r.descricao ?? '',
    responsavelId: r.responsavelId ?? undefined,
    prazo: r.prazo ?? undefined,
    status: r.status,
    evidenciaObrigatoria: r.evidenciaObrigatoria ?? false,
  };
}

function adaptComentario(r: any): Comentario {
  return {
    id: r.id,
    autorId: r.autorUsuarioId,
    data: r.criadoEm,
    texto: r.texto ?? '',
    tipo: r.tipo ?? 'COMENTARIO',
  };
}

function adaptEvidencia(r: any): Evidencia {
  return {
    id: r.id,
    atividadeId: r.atividadeId,
    arquivo: r.nomeArquivo ?? '',
    tipo: r.tipoMime ?? undefined,
    urlStorage: r.urlStorage ?? '',
    versao: r.versao ?? 1,
    usuarioId: r.enviadoPorUsuarioId,
    data: r.enviadoEm,
    statusValidacao: r.status,
    motivoRejeicao: r.motivoRejeicao ?? undefined,
  };
}

function adaptExcecao(r: any): Excecao {
  return {
    id: r.id,
    cicloId: r.cicloId,
    atividadeId: r.atividadeId,
    tipo: r.tipo,
    dependenciaId: r.dependenciaId ?? undefined,
    justificativa: r.justificativa ?? '',
    solicitanteUsuarioId: r.solicitanteUsuarioId,
    autorizadorId: r.autorizadorId,
    vigenciaAte: r.vigenciaAte,
    criadoEm: r.criadoEm,
    revogadaEm: r.revogadaEm ?? undefined,
    revogadaPorUsuarioId: r.revogadaPorUsuarioId ?? undefined,
    revogadaMotivo: r.revogadaMotivo ?? undefined,
    ativa: !!r.ativa,
  };
}

function adaptRegraNotificacao(r: any): RegraNotificacao {
  return {
    id: r.id,
    eventType: r.eventType ?? '',
    canalMaestro: !!r.canalMaestro,
    canalEmail: !!r.canalEmail,
    canalWhatsapp: !!r.canalWhatsapp,
    antecedenciaDiasUteis: r.antecedenciaDiasUteis ?? undefined,
    destinatarios: r.destinatarios ?? [],
    alertaInternoObrigatorio: !!r.alertaInternoObrigatorio,
    ativo: r.ativo !== false,
    criadoPorUsuarioId: r.criadoPorUsuarioId,
    criadoEm: r.criadoEm,
    atualizadoEm: r.atualizadoEm ?? undefined,
  };
}

function adaptTemplateNotificacao(r: any): TemplateNotificacao {
  return {
    id: r.id,
    eventType: r.eventType ?? '',
    canal: r.canal ?? '',
    versao: r.versao ?? 1,
    status: r.status ?? 'RASCUNHO',
    assunto: r.assunto ?? undefined,
    corpo: r.corpo ?? '',
    criadoPorUsuarioId: r.criadoPorUsuarioId,
    criadoEm: r.criadoEm,
    publicadoPorUsuarioId: r.publicadoPorUsuarioId ?? undefined,
    publicadoEm: r.publicadoEm ?? undefined,
  };
}

function adaptConfiguracaoCanais(r: any): ConfiguracaoCanais {
  return {
    id: r.id,
    emailHabilitado: r.emailHabilitado !== false,
    whatsappStatus: r.whatsappStatus ?? 'DESABILITADO',
    whatsappNumeroRemetente: r.whatsappNumeroRemetente ?? undefined,
    whatsappTokenConfigurado: !!r.whatsappTokenConfigurado,
    atualizadoPorUsuarioId: r.atualizadoPorUsuarioId ?? undefined,
    atualizadoEm: r.atualizadoEm ?? undefined,
  };
}

function adaptEntregaNotificacao(r: any): EntregaNotificacao {
  return {
    id: r.id,
    eventType: r.eventType ?? '',
    cicloId: r.cicloId ?? undefined,
    entidadeTipo: r.entidadeTipo ?? undefined,
    entidadeId: r.entidadeId ?? undefined,
    destinatarioUsuarioId: r.destinatarioUsuarioId,
    canal: r.canal ?? '',
    assunto: r.assunto ?? undefined,
    mensagem: r.mensagem ?? '',
    status: r.status ?? '',
    tentativaNumero: r.tentativaNumero ?? 1,
    erro: r.erro ?? undefined,
    fallback: !!r.fallback,
    criadoEm: r.criadoEm,
    enviadoEm: r.enviadoEm ?? undefined,
  };
}

function adaptLogAtividade(r: any): LogAtividade {
  return {
    id: r.id,
    atividadeId: r.atividadeId,
    usuarioId: r.usuarioId,
    acao: r.acao ?? '',
    statusAnterior: r.statusAnterior ?? undefined,
    statusNovo: r.statusNovo ?? undefined,
    detalhe: r.detalhe ?? undefined,
    criadoEm: r.criadoEm,
  };
}

function adaptDependencia(r: any): Dependencia {
  return {
    id: r.id,
    atividadeOrigemId: r.atividadeOrigemId,
    atividadeDestinoId: r.atividadeDestinoId,
    tipo: r.tipo ?? 'FIM_INICIO',
    bloqueante: r.bloqueante ?? true,
    caminhoCritico: r.caminhoCritico ?? false,
    motivo: r.motivo ?? '',
    areaResponsavelId: r.areaResponsavelId ?? 0,
    responsavelId: r.responsavelId ?? 0,
    prazoOriginal: r.prazoOriginal ?? '',
    previsaoAtual: r.previsaoAtual ?? '',
    quantidadeAtividadesImpactadas: 0,
    impactoNaPrevisaoFinalDias: 0,
    acaoRecomendada: '',
  };
}

/** Sem campo literal de "etapa PDCA" no backend — derivado do estado real (Status + já ter eficácia avaliada). */
function derivarEtapaPdca(status: string, temAvaliacaoEficacia: boolean): EtapaPDCA {
  if (status === 'ABERTO') return 'PLAN';
  if (status === 'EM_ANDAMENTO') return 'DO';
  if (status === 'CONCLUIDO') return temAvaliacaoEficacia ? 'ACT' : 'CHECK';
  return 'PLAN';
}

function adaptPdca(r: any, temAvaliacaoEficacia: boolean): AcaoPDCA {
  const statusMap: Record<string, AcaoPDCA['status']> = {
    ABERTO: 'ABERTA', EM_ANDAMENTO: 'EM_ANDAMENTO', CONCLUIDO: 'CONCLUIDA', CANCELADO: 'VENCIDA',
  };
  return {
    id: r.id,
    cicloOrigemId: r.cicloId,
    cicloAplicacaoId: undefined,
    etapa: derivarEtapaPdca(r.status, temAvaliacaoEficacia),
    problema: r.problema ?? '',
    causa: r.causa ?? '',
    areaResponsavelId: 0,
    responsavelId: r.responsavelId ?? 0,
    acaoCorretiva: r.acaoCorretiva ?? '',
    acaoPreventiva: '',
    prazo: r.prazoAcao ?? '',
    prioridade: 'NORMAL',
    metaEsperada: '',
    status: statusMap[r.status] ?? 'ABERTA',
    resultado: undefined,
    origemRecomendacaoId: undefined,
  };
}

function adaptRisco(r: any): Risco {
  return {
    id: r.id,
    cicloId: r.cicloId,
    atividadeId: r.atividadeId ?? undefined,
    empresaId: r.empresaId,
    titulo: r.titulo ?? '',
    descricao: r.descricao ?? undefined,
    severidade: r.severidade ?? 'MEDIA',
    status: r.status ?? 'ABERTO',
    recomendacaoAcao: r.recomendacaoAcao ?? undefined,
    responsavelId: r.responsavelId ?? undefined,
    prazoAcao: r.prazoAcao ?? undefined,
    criadoPorUsuarioId: r.criadoPorUsuarioId,
    criadoEm: r.criadoEm,
    atualizadoEm: r.atualizadoEm ?? undefined,
  };
}

function adaptEscalationEvent(r: any): RiscoEscalationEvent {
  return {
    id: r.id,
    riscoId: r.riscoId,
    nivelEscalonamento: r.nivelEscalonamento ?? '',
    motivo: r.motivo ?? '',
    destinatarioUsuarioId: r.destinatarioUsuarioId ?? undefined,
    usuarioId: r.usuarioId ?? undefined,
    criadoEm: r.criadoEm,
  };
}

function adaptDelegacao(r: any): Delegacao {
  return {
    id: r.id,
    empresaId: r.empresaId,
    delegadoDeUsuarioId: r.delegadoDeUsuarioId,
    delegadoParaUsuarioId: r.delegadoParaUsuarioId,
    dataInicio: r.dataInicio,
    dataFim: r.dataFim,
    motivo: r.motivo ?? '',
    status: r.status ?? 'ATIVA',
    ehVigente: !!r.ehVigente,
    criadoPorUsuarioId: r.criadoPorUsuarioId,
    criadoEm: r.criadoEm,
    revogadoPorUsuarioId: r.revogadoPorUsuarioId ?? undefined,
    revogadoEm: r.revogadoEm ?? undefined,
    revogadoMotivo: r.revogadoMotivo ?? undefined,
  };
}

function adaptPontuacao(r: any): PontuacaoArea {
  const classificacaoPorScore = (score: number): PontuacaoArea['classificacao'] => {
    if (score >= 95) return 'EXCELENCIA';
    if (score >= 80) return 'ALTO_DESEMPENHO';
    if (score >= 60) return 'BOM_DESEMPENHO';
    if (score >= 40) return 'EM_EVOLUCAO';
    return 'REQUER_ATENCAO';
  };
  return {
    id: r.responsavelId,
    areaId: 0,
    cicloId: 0,
    pontuacaoTotal: r.scoreMedio ?? 0,
    classificacao: classificacaoPorScore(r.scoreMedio ?? 0),
    posicaoRanking: r.posicao ?? 0,
    entregasNoPrazo: r.conclusaoNoPrazoQtd ?? 0,
    entregasAntecipadas: r.conclusaoAntecipadaQtd ?? 0,
    ausenciaRetrabalhoPercentual: r.atividadesAvaliadas ? Math.round(100 - (100 * (r.retrabalhosQtd ?? 0)) / r.atividadesAvaliadas) : 100,
    contribuicaoCaminhoCritico: 0,
    tendencia: 'ESTAVEL',
    evolucaoPontos: 0,
    criteriosDetalhe: [],
    integrantes: [],
  };
}

// O backend (MAESTRO-RN-18) trabalha com um gate simples de 4 etapas de configuração
// ("AREAS_PROCESSOS"/"TEMPLATES_SLA"/"EVIDENCIAS_REGRAS"/"REVISAO"), mais enxuto que as 20 etapas
// do wizard originalmente mockado — `tipo` é repassado como veio da API, sem forçar no enum antigo.
function adaptEtapaSetup(r: any, ordem: number): EtapaSetup {
  const tipo = r.tipo as TipoEtapaSetup;
  return {
    id: r.id,
    ordem,
    tipo,
    titulo: LABEL_ETAPA_SETUP[tipo] ?? tipo,
    descricao: DESCRICAO_ETAPA_SETUP[tipo] ?? '',
    concluida: r.concluida ?? false,
    percentualConcluido: r.concluida ? 100 : 0,
    responsavelId: r.concluidaPorUsuarioId ?? 0,
    responsavelNome: r.concluidaPorUsuarioNome ?? undefined,
    ultimaAtualizacao: r.concluidaEm ?? '',
    itensConcluidos: r.concluida ? 1 : 0,
    itensPendentes: r.concluida ? 0 : 1,
    evidenciaDescricao: r.evidenciaDescricao ?? undefined,
    pendencias: [],
  };
}

// ─── service ──────────────────────────────────────────────────────────────────

const maestroService = {
  // Escopo — Grupo Econômico / Grupo de Empresas / Empresa (seção 9.37 do handoff)
  async listarGruposEconomicos(): Promise<GrupoEconomico[]> {
    const { data } = await api.get(`${BASE}/grupos-economicos`);
    return (Array.isArray(data) ? data : []).map(adaptGrupoEconomico);
  },

  async criarGrupoEconomico(dto: { nome: string; responsavelId?: number }): Promise<GrupoEconomico> {
    const { data } = await api.post(`${BASE}/grupos-economicos`, dto);
    return adaptGrupoEconomico(data);
  },

  async listarGruposEmpresas(): Promise<GrupoEmpresas[]> {
    const { data } = await api.get(`${BASE}/grupos-empresas`);
    return (Array.isArray(data) ? data : []).map(adaptGrupoEmpresas);
  },

  async criarGrupoEmpresas(dto: { grupoEconomicoId: number; nome: string; empresaIds: number[] }): Promise<GrupoEmpresas> {
    const { data } = await api.post(`${BASE}/grupos-empresas`, dto);
    return adaptGrupoEmpresas(data);
  },

  // Empresas — reusa o endpoint já existente da plataforma (fora do namespace maestro/)
  async listarEmpresas(grupoEconomicoId: number, gruposEmpresas: GrupoEmpresas[]): Promise<Empresa[]> {
    const { data } = await api.get('Empresa', { params: { offset: 0, perPage: 400 } });
    const empresaIdParaGrupo = new Map<number, number>();
    gruposEmpresas.forEach(g => g.empresaIds.forEach(id => empresaIdParaGrupo.set(id, g.id)));
    const itens = Array.isArray(data) ? data : data?.itens ?? [];
    return itens.map((r: any) => adaptEmpresa(r, grupoEconomicoId, empresaIdParaGrupo));
  },

  async listarAreas(): Promise<Area[]> {
    const { data } = await api.get(`${BASE}/areas`);
    return (Array.isArray(data) ? data : []).map(adaptArea);
  },
  async criarArea(dto: { tipo: string; nome: string; responsavelId?: number; cor?: string; empresaIds: number[] }): Promise<Area> {
    const { data } = await api.post(`${BASE}/areas`, dto);
    return adaptArea(data);
  },

  // Processos (RN-19) — etapa de setup AREAS_PROCESSOS.
  async listarProcessos(): Promise<any[]> {
    const { data } = await api.get(`${BASE}/processes`);
    return Array.isArray(data) ? data : [];
  },
  async criarProcesso(dto: { areaId: number; nome: string; descricao?: string; responsavelId?: number; empresaIds: number[] }): Promise<any> {
    const { data } = await api.post(`${BASE}/processes`, dto);
    return data;
  },

  // Modelo de fechamento / versões de template (RN-17) — etapa de setup MODELO. `conteudoJson` é um
  // snapshot opaco exigido pelo backend (checksum de integridade) — os dados granulares de verdade
  // ficam nos itens (ver template-items abaixo), não nesse blob.
  async listarTemplateVersions(): Promise<any[]> {
    const { data } = await api.get(`${BASE}/templates`);
    return Array.isArray(data) ? data : [];
  },
  async criarTemplateVersion(nome: string, descricao?: string): Promise<any> {
    const conteudoJson = JSON.stringify({ nome, descricao: descricao ?? '', criadoEm: new Date().toISOString() });
    const { data } = await api.post(`${BASE}/templates`, { nome, descricao, origem: 'PERSONALIZADO', conteudoJson });
    return data;
  },

  // Pré-configuração padrão (RN-17) — cria de uma vez todas as áreas, processos e uma versão de
  // template (origem PADRAO_AI_NUMERA, status DRAFT) já com as atividades e dependências reais de
  // um fechamento contábil mensal. Nada fica ativo sozinho: é só um ponto de partida editável/
  // removível pelas próprias telas de Setup (mesmas usadas para cadastro manual).
  async aplicarPadraoSetup(empresaIds: number[]): Promise<{
    templateVersion: any; areasCriadas: number; processosCriados: number; itensCriados: number; dependenciasCriadas: number;
  }> {
    const { data } = await api.post(`${BASE}/setup/padrao`, { empresaIds });
    return data;
  },

  // Modelo de pontuação PON (RN-17/RN-38) — usado na abertura de ciclo para vincular o ScoreModelVersionId.
  async listarScoreModelVersions(): Promise<any[]> {
    const { data } = await api.get(`${BASE}/scoring/models`);
    return Array.isArray(data) ? data : [];
  },

  // Itens de template (RN-22) — etapas de setup TEMPLATES_SLA (prazoDiasUteis) e EVIDENCIAS_REGRAS
  // (evidenciaObrigatoria/aprovacaoSoxObrigatoria), mesmo endpoint e mesma entidade pros dois.
  async listarTemplateItems(templateVersionId: number): Promise<any[]> {
    const { data } = await api.get(`${BASE}/templates/${templateVersionId}/items`);
    return Array.isArray(data) ? data : [];
  },
  async criarTemplateItem(templateVersionId: number, dto: {
    codigo: string; nivelHierarquico: string; areaId?: number; processoId?: number; nome: string;
    descricao?: string; prazoDiasUteis: number; offsetInicioDiasUteis?: number;
    evidenciaObrigatoria: boolean; aprovacaoSoxObrigatoria: boolean;
  }): Promise<any> {
    const { data } = await api.post(`${BASE}/templates/${templateVersionId}/items`, { ...dto, offsetInicioDiasUteis: dto.offsetInicioDiasUteis ?? 0, prioridade: 'NORMAL', criticidade: 'MEDIA' });
    return data;
  },

  // Usuários vinculados de verdade ao Maestro (MaestroUsuarioMembro) — só ativos, é o que alimenta
  // todos os seletores de responsável/preparador/revisor/aprovador do módulo.
  async listarUsuarios(): Promise<UsuarioMaestro[]> {
    const { data } = await api.get(`${BASE}/usuarios-membros`);
    return (Array.isArray(data) ? data : []).map(adaptMembroMaestro);
  },

  // Todos os usuários do cliente na plataforma (mesmo endpoint do Workflow SOX) — usado só como
  // picker de "quem existe" ao adicionar um novo membro do Maestro, nunca nos seletores do módulo.
  async listarUsuariosPlataforma(clienteId: number): Promise<UsuarioMaestro[]> {
    const { data } = await api.get(`Usuario/BuscarPorClienteID/${clienteId}`);
    return (Array.isArray(data) ? data : []).map(adaptUsuario);
  },

  // Gestão de membros do Maestro (Setup — etapas PERFIS_NIVEIS/RESPONSAVEIS).
  async listarMembrosMaestro(): Promise<Array<{ membroId: number; usuarioId: number; nome: string; email: string; perfil: PerfilUsuario; areaId?: number; ativo: boolean }>> {
    const { data } = await api.get(`${BASE}/usuarios-membros`, { params: { somenteAtivos: false } });
    return (Array.isArray(data) ? data : []).map((r: any) => ({
      membroId: r.id,
      usuarioId: r.usuarioId,
      nome: r.nome ?? '—',
      email: r.email ?? '',
      perfil: (r.perfil as PerfilUsuario) ?? 'VISUALIZADOR',
      areaId: r.areaId ?? undefined,
      ativo: r.ativo !== false,
    }));
  },
  async adicionarMembroMaestro(usuarioId: number, perfil: PerfilUsuario, areaId?: number): Promise<void> {
    await api.post(`${BASE}/usuarios-membros`, { usuarioId, perfil, areaId });
  },
  async atualizarMembroMaestro(membroId: number, perfil: PerfilUsuario, areaId?: number): Promise<void> {
    await api.put(`${BASE}/usuarios-membros/${membroId}`, { perfil, areaId });
  },
  async removerMembroMaestro(membroId: number): Promise<void> {
    await api.delete(`${BASE}/usuarios-membros/${membroId}`);
  },

  // Gestão de escopo de acesso (MAESTRO-RN-20 — MOD-06). Restrito a Master no backend
  // (MaestroUserScopeService.GetByUsuarioAlvoAsync) — qualquer outro perfil recebe "Permissão negada".
  async listarEscopoUsuario(usuarioAlvoId: number): Promise<EscopoUsuarioMaestro[]> {
    const { data } = await api.get(`${BASE}/access/scopes/${usuarioAlvoId}`);
    return (Array.isArray(data) ? data : []).map(adaptEscopoUsuario);
  },
  async concederEscopo(dto: { usuarioId: number; empresaId: number; areaId?: number; nivel: NivelAcessoMaestro }): Promise<EscopoUsuarioMaestro> {
    const { data } = await api.post(`${BASE}/access/scopes`, dto);
    return adaptEscopoUsuario(data);
  },
  async revogarEscopo(scopeId: number): Promise<void> {
    await api.post(`${BASE}/access/scopes/${scopeId}/revoke`, {});
  },

  async listarFeriados(): Promise<Feriado[]> {
    const { data } = await api.get(`${BASE}/calendar`);
    const calendarios = Array.isArray(data) ? data : [];
    const feriados: Feriado[] = [];
    calendarios.forEach((cal: any) => {
      const tipo: Feriado['tipo'] = cal.tipo ?? 'NACIONAL';
      (cal.feriados ?? []).forEach((f: any) => feriados.push(adaptFeriado(f, tipo, cal.empresaId ?? undefined)));
    });
    return feriados;
  },

  async listarCalendarios(): Promise<CalendarioFeriados[]> {
    const { data } = await api.get(`${BASE}/calendar`);
    return (Array.isArray(data) ? data : []).map(adaptCalendario);
  },
  async criarCalendario(dto: { nome: string; tipo: string; uf?: string; municipio?: string; empresaId?: number }): Promise<CalendarioFeriados> {
    const { data } = await api.post(`${BASE}/calendar`, dto);
    return adaptCalendario(data);
  },
  async adicionarFeriado(calendarId: number, dto: { data: string; nome: string }): Promise<CalendarioFeriados> {
    const { data } = await api.post(`${BASE}/calendar/${calendarId}/holidays`, dto);
    return adaptCalendario(data);
  },
  async removerFeriado(holidayId: number): Promise<CalendarioFeriados> {
    const { data } = await api.delete(`${BASE}/calendar/holidays/${holidayId}`);
    return adaptCalendario(data);
  },
  async publicarCalendario(calendarId: number): Promise<CalendarioFeriados> {
    const { data } = await api.post(`${BASE}/calendar/${calendarId}/publish`, {});
    return adaptCalendario(data);
  },
  async sincronizarFeriadosBrasilApi(calendarId: number, ano: number): Promise<CalendarioFeriados> {
    const { data } = await api.post(`${BASE}/calendar/${calendarId}/sync-brasil-api`, { ano });
    return adaptCalendario(data);
  },
  async criarNovaVersaoCalendario(calendarId: number): Promise<CalendarioFeriados> {
    const { data } = await api.post(`${BASE}/calendar/${calendarId}/new-version`, {});
    return adaptCalendario(data);
  },

  async criarPdca(dto: { cicloId: number; empresaId: number; problema: string; acaoCorretiva: string; causa?: string; responsavelId?: number; prazoAcao?: string }): Promise<AcaoPDCA> {
    const { data } = await api.post(`${BASE}/pdca`, dto);
    return adaptPdca(data, false);
  },

  // MAESTRO-RN-37 — não existe campo literal de "etapa" no backend, ela é derivada do Status (ver
  // derivarEtapaPdca) — avançar a ação de Plan/Do/Check/Action é sempre uma troca de Status aqui
  // (ABERTO->EM_ANDAMENTO->CONCLUIDO) ou, na última etapa, registrar a avaliação de eficácia. A API
  // faz update completo (não merge), por isso reenviamos problema/ação/causa/etc. junto do status.
  async atualizarStatusPdca(id: number, acao: AcaoPDCA, novoStatus: 'ABERTO' | 'EM_ANDAMENTO' | 'CONCLUIDO' | 'CANCELADO'): Promise<AcaoPDCA> {
    const { data } = await api.put(`${BASE}/pdca/${id}`, {
      problema: acao.problema,
      causa: acao.causa || undefined,
      acaoCorretiva: acao.acaoCorretiva,
      responsavelId: acao.responsavelId || undefined,
      prazoAcao: acao.prazo || undefined,
      status: novoStatus,
    });
    // nunca há avaliação de eficácia registrada nesta chamada — só é possível registrá-la depois,
    // com o plano já concluído (ver registrarEficaciaPdca) — então CONCLUIDO aqui sempre vira CHECK.
    return adaptPdca(data, false);
  },

  async registrarEficaciaPdca(id: number, eficaz: boolean, observacao?: string): Promise<void> {
    await api.post(`${BASE}/pdca/${id}/effectiveness`, { eficaz, observacao: observacao || undefined });
  },

  // Ciclos
  async listarCiclos(): Promise<CicloFechamento[]> {
    const { data } = await api.get(`${BASE}/ciclos`);
    return (Array.isArray(data) ? data : []).map(adaptCiclo);
  },

  async encerrarCiclo(cicloId: number, payload?: { justificativa?: string; autorizadorId?: number; riscoId?: number; medidasCompensatorias?: Array<{ descricao: string; responsavelId: number; prazo: string }> }): Promise<CicloFechamento> {
    const { data } = await api.post(`${BASE}/ciclos/${cicloId}/encerrar`, payload ?? {});
    return adaptCiclo(data);
  },

  async reabrirCiclo(cicloId: number, motivo: string): Promise<CicloFechamento> {
    const { data } = await api.post(`${BASE}/ciclos/${cicloId}/reabrir`, { motivo });
    return adaptCiclo(data);
  },

  async abrirCiclo(
    competencia: string,
    empresaIds: number[],
    prazoOficial: string,
    previsaoConclusao?: string,
    templateVersionId?: number,
    scoreModelVersionId?: number,
    calendarVersionId?: number,
  ): Promise<CicloFechamento> {
    const { data } = await api.post(`${BASE}/ciclos`, {
      competencia,
      empresaIds,
      prazoOficial,
      previsaoConclusao: previsaoConclusao || undefined,
      templateVersionId,
      scoreModelVersionId,
      calendarVersionId,
    });
    return adaptCiclo(data);
  },

  // "Ciclo pronto para encerrar" (StatusCiclo do frontend) — sem coluna própria no backend; deriva
  // da mesma validação de checklist que EncerrarAsync usa, exposta aqui somente-leitura.
  async obterProntidaoEncerramento(cicloId: number): Promise<{ pronto: boolean; pendencias: string[] }> {
    const { data } = await api.get(`${BASE}/ciclos/${cicloId}/prontidao-encerramento`);
    return { pronto: data?.pronto ?? false, pendencias: data?.pendencias ?? [] };
  },

  // Atividades
  async listarAtividadesPorCiclo(cicloId: number): Promise<Atividade[]> {
    const { data } = await api.get(`${BASE}/atividades/ciclo/${cicloId}`);
    return (Array.isArray(data) ? data : []).map(adaptAtividade);
  },

  // Dependências
  async listarDependenciasPorCiclo(cicloId: number): Promise<Dependencia[]> {
    const { data } = await api.get(`${BASE}/dependencias/ciclo/${cicloId}`);
    return (Array.isArray(data) ? data : []).map(adaptDependencia);
  },

  // Riscos (MAESTRO-RN-36)
  async listarRiscosPorCiclo(cicloId: number): Promise<Risco[]> {
    const { data } = await api.get(`${BASE}/riscos/ciclo/${cicloId}`);
    return (Array.isArray(data) ? data : []).map(adaptRisco);
  },
  async criarRisco(dto: { cicloId: number; atividadeId?: number; empresaId: number; titulo: string; descricao?: string; severidade: string }): Promise<Risco> {
    const { data } = await api.post(`${BASE}/riscos`, dto);
    return adaptRisco(data);
  },
  async atualizarRisco(id: number, dto: { titulo: string; descricao?: string; severidade: string; status: string; recomendacaoAcao?: string; responsavelId?: number; prazoAcao?: string }): Promise<Risco> {
    const { data } = await api.put(`${BASE}/riscos/${id}`, dto);
    return adaptRisco(data);
  },
  async escalonarRisco(id: number, dto: { nivelEscalonamento: string; motivo: string; destinatarioUsuarioId?: number }): Promise<Risco> {
    const { data } = await api.post(`${BASE}/riscos/${id}/escalonar`, dto);
    return adaptRisco(data);
  },
  async listarEscalonamentosDoRisco(id: number): Promise<RiscoEscalationEvent[]> {
    const { data } = await api.get(`${BASE}/riscos/${id}/escalation-events`);
    return (Array.isArray(data) ? data : []).map(adaptEscalationEvent);
  },

  // Delegação (MAESTRO-RN-34)
  async listarDelegacoesConcedidas(): Promise<Delegacao[]> {
    const { data } = await api.get(`${BASE}/delegacoes/concedidas`);
    return (Array.isArray(data) ? data : []).map(adaptDelegacao);
  },
  async listarDelegacoesRecebidas(): Promise<Delegacao[]> {
    const { data } = await api.get(`${BASE}/delegacoes/recebidas`);
    return (Array.isArray(data) ? data : []).map(adaptDelegacao);
  },
  async criarDelegacao(dto: { delegadoParaUsuarioId: number; empresaId: number; dataInicio: string; dataFim: string; motivo: string }): Promise<Delegacao> {
    const { data } = await api.post(`${BASE}/delegacoes`, dto);
    return adaptDelegacao(data);
  },
  async revogarDelegacao(id: number, motivo?: string): Promise<Delegacao> {
    const { data } = await api.post(`${BASE}/delegacoes/${id}/revogar`, { motivo });
    return adaptDelegacao(data);
  },

  // PDCA
  async listarPdcaPorCiclo(cicloId: number): Promise<AcaoPDCA[]> {
    const { data } = await api.get(`${BASE}/pdca/ciclo/${cicloId}`);
    const planos = Array.isArray(data) ? data : [];
    const resultados = await Promise.all(planos.map(async (p: any) => {
      if (p.status !== 'CONCLUIDO') return adaptPdca(p, false);
      try {
        const { data: avaliacoes } = await api.get(`${BASE}/pdca/${p.id}/effectiveness`);
        return adaptPdca(p, Array.isArray(avaliacoes) && avaliacoes.length > 0);
      } catch {
        return adaptPdca(p, false);
      }
    }));
    return resultados;
  },

  // "PDCA gerando atividade real" — cria uma MaestroAtividade a partir da ação corretiva do plano.
  async gerarAtividadeDoPdca(pdcaPlanId: number, areaId: number): Promise<{ atividadeId: number }> {
    const { data } = await api.post(`${BASE}/pdca/${pdcaPlanId}/gerar-atividade`, { areaId });
    return { atividadeId: data?.atividadeId };
  },

  // Ranking / Pontuação
  async listarRankingPorCiclo(cicloId: number): Promise<PontuacaoArea[]> {
    const { data } = await api.get(`${BASE}/ranking/ciclo/${cicloId}`);
    return (Array.isArray(data) ? data : []).map(adaptPontuacao);
  },

  // Setup do módulo
  async obterSetup(): Promise<{ status: string; percentualConcluido: number; etapas: EtapaSetup[] }> {
    const { data } = await api.get(`${BASE}/setup`);
    return {
      status: data?.status ?? 'AGUARDANDO_CONFIGURACAO',
      percentualConcluido: data?.percentualConcluido ?? 0,
      etapas: (data?.etapas ?? []).map((e: any, i: number) => adaptEtapaSetup(e, i + 1)),
    };
  },

  async concluirEtapaSetup(etapaId: number, evidenciaDescricao: string): Promise<void> {
    await api.put(`${BASE}/setup/etapas/${etapaId}/concluir`, { evidenciaDescricao });
  },

  async reabrirEtapaSetup(etapaId: number): Promise<void> {
    await api.put(`${BASE}/setup/etapas/${etapaId}/reabrir`, {});
  },

  async ativarSetup(): Promise<{ status: string; percentualConcluido: number; pendencias: string[] }> {
    const { data } = await api.post(`${BASE}/setup/activate`, {});
    return data;
  },

  async homologarSetup(observacoes?: string): Promise<void> {
    await api.post(`${BASE}/setup/homologar`, { observacoes });
  },

  // Restrito a Master no backend (MaestroModuleSetupService.ResetarAsync) — zera as 8 etapas e o
  // status do setup para reconfigurar do zero, sem afetar ciclos/atividades já existentes.
  async resetarSetup(justificativaSox: string): Promise<void> {
    await api.post(`${BASE}/setup/reset`, { justificativaSox });
  },

  // Tela "Clientes" (Master) — mesmo padrão do workflowService.onboardingStatusBatch/resetOnboarding,
  // reset por clienteId explícito (não o cliente do usuário logado).
  async obterSetupStatusBatch(clienteIds: number[]): Promise<Array<{ clienteId: number; status: string; percentualConcluido: number; etapasConcluidas: number; etapasTotais: number }>> {
    if (clienteIds.length === 0) return [];
    const { data } = await api.get(`${BASE}/setup/status-batch`, { params: { clienteIds: clienteIds.join(',') } });
    return Array.isArray(data) ? data : [];
  },
  async resetarSetupPorCliente(clienteId: number, justificativaSox: string): Promise<void> {
    await api.post(`${BASE}/setup/${clienteId}/reset`, { justificativaSox });
  },

  // Relatório mensal — campos sem equivalente no backend (RN-42 cobre atrasos/antecipações/
  // dependências/ranking/PDCA/eficácia/decisões; não cobre indicadores cross-produto como
  // impactos fiscais/financeiros ou variações do Mapa de Resultados) recebem zero/vazio honesto.
  async obterRelatorioMensal(cicloId: number, ciclo?: CicloFechamento, pontuacoes?: PontuacaoArea[]): Promise<RelatorioMensal | null> {
    const { data } = await api.get(`${BASE}/reports/monthly/ciclo/${cicloId}`);
    const versao = Array.isArray(data) ? data[data.length - 1] : data;
    if (!versao) return null;
    const conteudo = versao.conteudo ?? {};
    // "Áreas que mais contribuíram/exigem atenção" derivadas da mesma pontuação real (PON_V1) usada
    // no Ranking — mesmo critério de classificação já usado nos cards do Cockpit (ver
    // MaestroCockpitPage: areasContribuiram/areasAtencao) — não é um cálculo próprio do relatório.
    const pontuacoesDoCiclo = pontuacoes ?? [];
    return {
      id: versao.id,
      cicloId: versao.cicloId,
      escopoResumo: ciclo ? `${ciclo.empresaIds.length} empresa(s) — ${ciclo.competencia}` : '',
      empresaIds: ciclo?.empresaIds ?? [],
      competencia: ciclo?.competencia ?? '',
      prazoOficial: ciclo?.prazoOficial ?? '',
      dataReal: ciclo?.dataEncerramentoReal,
      resultadoCiclo: versao.status ?? '',
      progresso: ciclo?.progressoRealizado ?? 0,
      entregasNoPrazo: conteudo.concluidasNoPrazo ?? 0,
      entregasAntecipadas: conteudo.concluidasAntecipadas ?? 0,
      entregasAtrasadas: conteudo.concluidasAtrasadas ?? 0,
      principaisDependencias: (conteudo.dependenciasImpactadas ?? []).map((d: any) => `#${d.dependenciaId}`),
      areasQueMaisContribuiramIds: pontuacoesDoCiclo.filter(p => p.classificacao === 'EXCELENCIA' || p.classificacao === 'ALTO_DESEMPENHO').map(p => p.areaId),
      areasQueExigemAtencaoIds: pontuacoesDoCiclo.filter(p => p.classificacao === 'REQUER_ATENCAO').map(p => p.areaId),
      principaisAtrasos: (conteudo.atrasos ?? []).map((a: any) => ({ atividade: a.nome ?? a.codigo ?? '—', causa: a.justificativa ?? '' })),
      retrabalhos: 0,
      achadosRevisor: 0,
      impactosFiscais: 0,
      impactosFinanceiros: 0,
      variacoesMapaResultados: 0,
      situacaoConciliacoes: '',
      situacaoControlesSox: '',
      acoesPDCAIds: (conteudo.pdcaAtual ?? []).map((p: any) => p.id),
      geradoEm: versao.geradoEm ?? '',
      comparacaoCicloAnterior: [],
    };
  },

  // Central de logs — combina histórico de atividade + entregas de notificação + decisões de SoD
  // do ciclo, todas trilhas append-only reais (GET /api/maestro/ciclos/{id}/logs). empresaId/areaId
  // são derivados no cliente a partir do atividadeId (não vêm do backend).
  async listarLogsPorCiclo(cicloId: number, atividades: Atividade[]): Promise<LogEntry[]> {
    const { data } = await api.get(`${BASE}/ciclos/${cicloId}/logs`);
    const porAtividadeId = new Map(atividades.map(a => [a.id, a]));
    return (Array.isArray(data) ? data : []).map((r: any): LogEntry => {
      const atividade = r.atividadeId ? porAtividadeId.get(r.atividadeId) : undefined;
      const origem: OrigemLog = r.origem ?? 'ATIVIDADE_HISTORICO';
      const severidade: SeveridadeLog = origem === 'SOD_DECISION' && r.statusNovo === 'NEGADO' ? 'CRITICA'
        : origem === 'NOTIFICACAO' && String(r.acao ?? '').includes('_FALHA') ? 'ATENCAO'
        : 'INFO';
      return {
        origem,
        atividadeId: r.atividadeId ?? undefined,
        atividadeCodigo: r.atividadeCodigo ?? undefined,
        usuarioId: r.usuarioId,
        acao: r.acao ?? '',
        detalhe: r.detalhe ?? undefined,
        statusAnterior: r.statusAnterior ?? undefined,
        statusNovo: r.statusNovo ?? undefined,
        criadoEm: r.criadoEm,
        empresaId: atividade?.empresaId,
        areaId: atividade?.areaId,
        severidade,
      };
    });
  },

  // Checklist da atividade
  async listarChecklistPorAtividade(atividadeId: number): Promise<ChecklistItem[]> {
    const { data } = await api.get(`${BASE}/atividades/${atividadeId}/checklist`);
    return (Array.isArray(data) ? data : []).map(adaptChecklistItem);
  },
  async criarItemChecklist(atividadeId: number, descricao: string, responsavelId?: number, prazo?: string, evidenciaObrigatoria = false): Promise<ChecklistItem> {
    const { data } = await api.post(`${BASE}/atividades/${atividadeId}/checklist`, { descricao, responsavelId, prazo, evidenciaObrigatoria });
    return adaptChecklistItem(data);
  },
  async atualizarStatusChecklistItem(itemId: number, status: string): Promise<ChecklistItem> {
    const { data } = await api.patch(`${BASE}/checklist/${itemId}/status`, { status });
    return adaptChecklistItem(data);
  },
  async removerItemChecklist(itemId: number): Promise<void> {
    await api.delete(`${BASE}/checklist/${itemId}`);
  },

  // Comunicação (comentários) da atividade
  async listarComentariosPorAtividade(atividadeId: number): Promise<Comentario[]> {
    const { data } = await api.get(`${BASE}/atividades/${atividadeId}/comentarios`);
    return (Array.isArray(data) ? data : []).map(adaptComentario);
  },
  async criarComentario(atividadeId: number, texto: string, tipo = 'COMENTARIO'): Promise<Comentario> {
    const { data } = await api.post(`${BASE}/atividades/${atividadeId}/comentarios`, { texto, tipo });
    return adaptComentario(data);
  },

  // Evidências da atividade
  async listarEvidenciasPorAtividade(atividadeId: number): Promise<Evidencia[]> {
    const { data } = await api.get(`${BASE}/atividades/${atividadeId}/evidencias`);
    return (Array.isArray(data) ? data : []).map(adaptEvidencia);
  },
  async anexarEvidencia(atividadeId: number, arquivo: File): Promise<Evidencia> {
    const formData = new FormData();
    formData.append('arquivo', arquivo);
    const { data } = await api.post(`${BASE}/atividades/${atividadeId}/evidencias`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return adaptEvidencia(data);
  },
  async validarEvidencia(evidenciaId: number): Promise<Evidencia> {
    const { data } = await api.post(`${BASE}/evidencias/${evidenciaId}/validar`, {});
    return adaptEvidencia(data);
  },
  async rejeitarEvidencia(evidenciaId: number, motivo: string): Promise<Evidencia> {
    const { data } = await api.post(`${BASE}/evidencias/${evidenciaId}/rejeitar`, { motivo });
    return adaptEvidencia(data);
  },

  // Histórico (trilha de participação) da atividade
  async listarHistoricoPorAtividade(atividadeId: number): Promise<LogAtividade[]> {
    const { data } = await api.get(`${BASE}/atividades/${atividadeId}/history`);
    return (Array.isArray(data) ? data : []).map(adaptLogAtividade);
  },

  // Exceções formais a regras de bloqueio da atividade (MAESTRO-RN-44)
  async listarExcecoesPorAtividade(atividadeId: number): Promise<Excecao[]> {
    const { data } = await api.get(`${BASE}/atividades/${atividadeId}/exceptions`);
    return (Array.isArray(data) ? data : []).map(adaptExcecao);
  },
  async concederExcecao(atividadeId: number, dto: { tipo: string; dependenciaId?: number; justificativa: string; autorizadorId: number; vigenciaAte: string }): Promise<Excecao> {
    const { data } = await api.post(`${BASE}/atividades/${atividadeId}/exceptions`, dto);
    return adaptExcecao(data);
  },
  async revogarExcecao(excecaoId: number, motivo: string): Promise<Excecao> {
    const { data } = await api.post(`${BASE}/exceptions/${excecaoId}/revogar`, { motivo });
    return adaptExcecao(data);
  },

  // Notificações (MAESTRO-RN-43) — regras por evento, templates versionados, canais (e-mail/WhatsApp),
  // envio de teste e fila real de entregas.
  async listarRegrasNotificacao(): Promise<RegraNotificacao[]> {
    const { data } = await api.get(`${BASE}/notifications/configuration`);
    return (Array.isArray(data) ? data : []).map(adaptRegraNotificacao);
  },
  async salvarRegraNotificacao(dto: {
    eventType: string; canalMaestro: boolean; canalEmail: boolean; canalWhatsapp: boolean;
    antecedenciaDiasUteis?: number; destinatarios?: string[]; alertaInternoObrigatorio: boolean; ativo: boolean;
  }): Promise<RegraNotificacao> {
    const { data } = await api.put(`${BASE}/notifications/configuration`, dto);
    return adaptRegraNotificacao(data);
  },
  async listarTemplatesNotificacao(eventType: string, canal?: string): Promise<TemplateNotificacao[]> {
    const { data } = await api.get(`${BASE}/notifications/templates`, { params: { eventType, canal } });
    return (Array.isArray(data) ? data : []).map(adaptTemplateNotificacao);
  },
  async criarTemplateNotificacao(dto: { eventType: string; canal: string; assunto?: string; corpo: string }): Promise<TemplateNotificacao> {
    const { data } = await api.post(`${BASE}/notifications/templates`, dto);
    return adaptTemplateNotificacao(data);
  },
  async publicarTemplateNotificacao(templateVersionId: number): Promise<TemplateNotificacao> {
    const { data } = await api.post(`${BASE}/notifications/templates/${templateVersionId}/publicar`, {});
    return adaptTemplateNotificacao(data);
  },
  async obterConfiguracaoCanais(): Promise<ConfiguracaoCanais> {
    const { data } = await api.get('/api/maestro/channels/whatsapp/setup');
    return adaptConfiguracaoCanais(data);
  },
  async atualizarConfiguracaoCanais(dto: { emailHabilitado: boolean; whatsappStatus: string; whatsappNumeroRemetente?: string; whatsappTokenConfigurado: boolean }): Promise<ConfiguracaoCanais> {
    const { data } = await api.put('/api/maestro/channels/whatsapp/setup', dto);
    return adaptConfiguracaoCanais(data);
  },
  async testarNotificacao(dto: { eventType: string; canal: string; destinatarioUsuarioId: number }): Promise<EntregaNotificacao[]> {
    const { data } = await api.post(`${BASE}/notifications/test`, dto);
    return (Array.isArray(data) ? data : []).map(adaptEntregaNotificacao);
  },
  async listarEntregasNotificacao(cicloId?: number, status?: string): Promise<EntregaNotificacao[]> {
    const { data } = await api.get(`${BASE}/notifications/deliveries`, { params: { cicloId, status } });
    return (Array.isArray(data) ? data : []).map(adaptEntregaNotificacao);
  },

  // Cards dos produtos integrados — dado real de cada módulo, sem fabricar nenhum campo que o
  // endpoint de origem não devolve (ver models/produtos.ts).
  async obterResumoMotorConciliacao(empresaId: number, cicloId: number, competencia: string): Promise<MotorConciliacaoDados> {
    // Competência do Maestro é "MM/yyyy" (ex.: "07/2026"); o dashboard espera "yyyy-MM".
    const [mesStr, anoStr] = competencia.split('/');
    const mesFormatado = mesStr && anoStr ? `${anoStr}-${mesStr}` : undefined;
    const { data } = await api.get(`/api/Dashboard/ConciliacaoContabilFinanceira/${empresaId}/resumo`, { params: mesFormatado ? { mes: mesFormatado } : {} });
    const porCategoria = Array.isArray(data?.porCategoria) ? data.porCategoria : [];
    const contasConcluidas = porCategoria.reduce((s: number, c: any) => s + (c.conciliado ?? 0), 0);
    const contasComDivergencia = porCategoria.reduce((s: number, c: any) => s + (c.conciliadoComPendencia ?? 0), 0);
    const contasPendentesBruto = porCategoria.reduce((s: number, c: any) => s + (c.pendentes ?? 0), 0);
    const contasTotais = contasConcluidas + contasComDivergencia + contasPendentesBruto;
    return {
      tipo: 'MOTOR_CONCILIACAO',
      empresaId,
      cicloId,
      percentualProcessado: contasTotais ? Math.round((contasConcluidas / contasTotais) * 100) : 0,
      contasTotais,
      contasConcluidas,
      contasPendentes: data?.contasPendentes ?? contasPendentesBruto,
      contasComDivergencia,
      diferencaAConciliar: (data?.saldoBalancete ?? 0) - (data?.saldoComSuporte ?? 0),
    };
  },
  // Resolve a esteira (período de conciliação) da empresa que contém a competência do ciclo do
  // Maestro ("MM/yyyy") — Mapa de Resultado e Revisor Contábil são organizados por esteira, não por
  // ciclo do Maestro; sem essa ponte não há como saber qual período consultar nesses dois módulos.
  async resolverEsteiraId(empresaId: number, competencia: string): Promise<number | undefined> {
    const [mesStr, anoStr] = competencia.split('/');
    if (!mesStr || !anoStr) return undefined;
    const dataReferencia = new Date(Number(anoStr), Number(mesStr) - 1, 15);

    const { data } = await api.get(`/api/Esteira/empresa/${empresaId}`);
    const esteiras = Array.isArray(data) ? data : [];
    const encontrada = esteiras.find((e: any) => {
      const inicio = new Date(e.periodoInicio);
      const fim = new Date(e.periodoFim);
      return dataReferencia >= inicio && dataReferencia <= fim;
    });
    return encontrada?.id;
  },
  async obterResumoMapaResultados(empresaId: number, cicloId: number, competencia: string): Promise<MapaResultadosDados | null> {
    const esteiraId = await maestroService.resolverEsteiraId(empresaId, competencia);
    if (!esteiraId) return null;

    const { data } = await api.get(`/api/Esteira/${esteiraId}/resumo-resultado`);
    return {
      tipo: 'MAPA_RESULTADOS',
      empresaId,
      cicloId,
      contasTotais: data?.totalContas ?? 0,
      contasConcluidas: data?.concluidas ?? 0,
      contasPendentes: data?.pendentes ?? 0,
      contasComDivergencia: data?.comDivergencia ?? 0,
      percentualConcluido: data?.percentualConcluido ?? 0,
    };
  },
  async obterResumoRevisorContabil(empresaId: number, cicloId: number, competencia: string): Promise<RevisorContabilDados | null> {
    const esteiraId = await maestroService.resolverEsteiraId(empresaId, competencia);
    if (!esteiraId) return null;

    const { data } = await api.get('/api/auditoria/resumo-consolidado', { params: { empresaIds: String(empresaId), periodo: String(esteiraId) } });
    return {
      tipo: 'REVISOR_CONTABIL',
      empresaId,
      cicloId,
      modulosAvaliados: data?.modulosAvaliados ?? 0,
      modulosComFalha: data?.modulosComFalha ?? 0,
      erros: data?.totalErros ?? 0,
      alertas: data?.totalAlertas ?? 0,
      avisos: data?.totalAvisos ?? 0,
      impactoTotalEstimado: data?.impactoTotalEstimado ?? 0,
    };
  },
  async obterResumoWorkflowSox(empresaId: number, cicloId: number): Promise<WorkflowSoxDados> {
    const { data } = await api.get('/api/workflow/monitoramento');
    const itens = (Array.isArray(data?.itens) ? data.itens : []).filter((i: any) => i.tenantId === empresaId);
    return {
      tipo: 'WORKFLOW_SOX',
      empresaId,
      cicloId,
      itensNaFila: itens.length,
      emAlerta: itens.filter((i: any) => i.slaStatus === 'AMARELO').length,
      foraDoPrazo: itens.filter((i: any) => i.slaStatus === 'VERMELHO').length,
    };
  },

  // Apuração tributária e documentos fiscais (fluxo Comercial → Faturamento → Fiscal)
  async listarDocumentosFiscais(cicloId: number): Promise<any[]> {
    const { data } = await api.get(`${BASE}/ciclos/${cicloId}/documentos-fiscais`);
    return Array.isArray(data) ? data : [];
  },
  async criarDocumentoFiscal(payload: any): Promise<any> {
    const { data } = await api.post(`${BASE}/documentos-fiscais`, payload);
    return data;
  },
  async atualizarStatusDocumentoFiscal(id: number, status: string, rejeicoes?: string[]): Promise<any> {
    const { data } = await api.patch(`${BASE}/documentos-fiscais/${id}/status`, { status, rejeicoes });
    return data;
  },
  async listarApuracoesTributarias(cicloId: number): Promise<any[]> {
    const { data } = await api.get(`${BASE}/ciclos/${cicloId}/apuracoes-tributarias`);
    return Array.isArray(data) ? data : [];
  },
  async criarApuracaoTributaria(payload: any): Promise<any> {
    const { data } = await api.post(`${BASE}/apuracoes-tributarias`, payload);
    return data;
  },
  async atualizarStatusApuracaoTributaria(id: number, status: { statusApuracao?: string; statusContabilizacao?: string; statusConciliacao?: string }): Promise<any> {
    const { data } = await api.patch(`${BASE}/apuracoes-tributarias/${id}/status`, status);
    return data;
  },
};

export default maestroService;
